import { z } from 'zod';
import type { OrderStatus } from '../constants/index.js';

/**
 * Checkout tidak menerima harga dari klien sama sekali.
 * Yang dikirim hanya pilihan; seluruh nominal dihitung ulang di server
 * dari keranjang dan dari quote ongkir yang divalidasi ke penyedia.
 */
export const checkoutSchema = z.object({
  /** Alamat tersimpan milik pelanggan. */
  addressId: z.string().min(1).max(64),
  /** Kurir dan layanan hasil pilihan dari /api/shipping/quote. */
  courier: z.string().min(2).max(24),
  service: z.string().min(1).max(40),
  customerNote: z.string().max(500).optional(),
});

export const orderParamsSchema = z.object({
  orderNumber: z.string().min(4).max(40),
});

export const orderPaymentParamsSchema = orderParamsSchema.extend({
  paymentId: z.string().min(1).max(64),
});

export const ORDER_STATUSES = [
  'DRAFT',
  'PENDING_PAYMENT',
  'PAID',
  'IN_REVIEW',
  'IN_PRODUCTION',
  'SHIPPED',
  'COMPLETED',
  'CANCELLED',
] as const satisfies readonly OrderStatus[];

export const orderStatusSchema = z.enum(ORDER_STATUSES);

export const orderListQuerySchema = z.object({
  status: orderStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(50).default(10),
});

export type CheckoutInput = z.infer<typeof checkoutSchema>;
export type OrderParams = z.infer<typeof orderParamsSchema>;
export type OrderListQuery = z.infer<typeof orderListQuerySchema>;

/** Batas waktu bayar sejak pesanan dibuat. */
export const ORDER_PAYMENT_TTL_HOURS = 24;

// --- Admin ---

/**
 * Transisi status yang boleh dilakukan admin/staf. Satu sumber kebenaran:
 * server menolak yang tidak ada di sini, UI hanya menampilkan yang ada.
 *
 * PENDING_PAYMENT -> PAID sengaja tidak ada: pelunasan hanya lewat modul
 * pembayaran supaya tiap pesanan lunas selalu punya jejak transaksi.
 */
export const ORDER_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  DRAFT: [],
  PENDING_PAYMENT: ['CANCELLED'],
  PAID: ['IN_REVIEW', 'IN_PRODUCTION', 'CANCELLED'],
  IN_REVIEW: ['IN_PRODUCTION', 'CANCELLED'],
  IN_PRODUCTION: ['SHIPPED'],
  SHIPPED: ['COMPLETED'],
  COMPLETED: [],
  CANCELLED: [],
};

/**
 * Filter status desain di daftar pesanan admin:
 *  - missing  : ada item yang belum punya desain
 *  - pending  : ada desain yang belum disetujui tim CI
 *  - approved : semua item punya desain dan semuanya sudah disetujui
 */
export const ORDER_DESIGN_FILTERS = ['missing', 'pending', 'approved'] as const;
export type OrderDesignFilter = (typeof ORDER_DESIGN_FILTERS)[number];

/**
 * Masalah pembayaran yang perlu dicek admin, dihitung dari data pesanan:
 *  - OVERPAID          : uang masuk melebihi total (mis. dua tagihan sama-sama dibayar)
 *  - PAID_ON_CANCELLED : ada uang masuk tetapi pesanan sudah dibatalkan
 *  - UNCONFIRMED       : tagihan masih PENDING padahal sudah lewat waktu hangus,
 *                        artinya callback penyedia tidak pernah tiba
 */
export const ORDER_PAYMENT_ISSUES = ['OVERPAID', 'PAID_ON_CANCELLED', 'UNCONFIRMED'] as const;
export type OrderPaymentIssue = (typeof ORDER_PAYMENT_ISSUES)[number];

/** Jeda setelah tagihan hangus sebelum dianggap tak terkonfirmasi; callback bisa telat. */
export const PAYMENT_UNCONFIRMED_GRACE_MS = 10 * 60 * 1000;

export function orderPaymentIssues(
  order: {
    status: OrderStatus;
    total: number;
    amountPaid: number;
    payments: { status: string; expiresAt: Date | string | null }[];
  },
  now: Date = new Date(),
): OrderPaymentIssue[] {
  const issues: OrderPaymentIssue[] = [];
  if (order.amountPaid > order.total) issues.push('OVERPAID');
  if (order.status === 'CANCELLED' && order.amountPaid > 0) issues.push('PAID_ON_CANCELLED');
  const cutoff = now.getTime() - PAYMENT_UNCONFIRMED_GRACE_MS;
  if (
    order.payments.some(
      (p) => p.status === 'PENDING' && p.expiresAt && new Date(p.expiresAt).getTime() < cutoff,
    )
  ) {
    issues.push('UNCONFIRMED');
  }
  return issues;
}

export const adminOrderQuerySchema = z.object({
  status: orderStatusSchema.optional(),
  design: z.enum(ORDER_DESIGN_FILTERS).optional(),
  /** `issue` = hanya pesanan dengan masalah pembayaran. */
  payment: z.enum(['issue']).optional(),
  /**
   * Antrean kirim ke ImajiPRO: `stuck` = sudah menyerah dan perlu dicek,
   * `pending` = masih menunggu/dicoba lagi, `sent` = sudah terkirim.
   */
  sync: z.enum(['stuck', 'pending', 'sent']).optional(),
  /** Nomor pesanan, nama penerima, atau email pelanggan. */
  q: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const adminOrderStatusSchema = z.object({
  to: orderStatusSchema,
  note: z.string().trim().max(500).optional(),
  /** Wajib saat menuju SHIPPED bila pesanan belum punya resi. */
  trackingNumber: z.string().trim().min(3).max(64).optional(),
});

export const adminOrderUpdateSchema = z
  .object({
    internalNote: z.string().trim().max(2000).optional(),
    trackingNumber: z.string().trim().max(64).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Tidak ada yang diubah',
  });

export type AdminOrderQuery = z.infer<typeof adminOrderQuerySchema>;
export type AdminOrderStatusInput = z.infer<typeof adminOrderStatusSchema>;
export type AdminOrderUpdate = z.infer<typeof adminOrderUpdateSchema>;
